import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LayoutDashboard } from 'lucide-react';
import { navigationLinks, contactInfo } from '../mockData';
import { useAuth } from '../hooks/useAuth';
import { useChristmasTheme } from '../hooks/useChristmasTheme';
import { useRepublicDayTheme } from '../hooks/useRepublicDayTheme';
import { HeaderDecorations } from './christmas';
import { TricolorDecorations } from './republic-day';
import logoBanner from '../assets/images/logo-banner.png';

const Header = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { isChristmasMode } = useChristmasTheme();
  const { isRepublicDayMode } = useRepublicDayTheme();
  
  // Check if current page is public (not dashboard)
  const isPublicPage = !location.pathname.startsWith('/dashboard');

  return (
    <>
      {/* Top Bar */}
      <div className="bg-black text-white py-2 px-6 flex justify-between items-center text-sm">
        <div>SEBI Registration No. {contactInfo.sebiReg}</div>
        <div className="flex items-center gap-4">
          <a href={`tel:${contactInfo.phone}`} className="hover:text-yellow-400 transition-colors">
            {contactInfo.phone}
          </a>
          <span>|</span>
          <a href={`mailto:${contactInfo.email}`} className="hover:text-yellow-400 transition-colors">
            {contactInfo.email}
          </a>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-[#1b2d4a] text-white sticky top-0 z-50 shadow-lg relative">
        {/* Christmas/Republic Day Decorations */}
        {isPublicPage && isChristmasMode && <HeaderDecorations />}
        {isPublicPage && isRepublicDayMode && <TricolorDecorations />}
        
        <div className="container mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img
                src={logoBanner}
                alt="Climb Capital Services Private Limited"
                className="h-20 w-auto"
              />
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-6">
              {navigationLinks.map((link) => (
                <div key={link.name} className="relative">
                  {link.dropdown ? (
                    <div
                      className="relative group"
                      onMouseEnter={() => setServicesOpen(true)}
                      onMouseLeave={() => setServicesOpen(false)}
                    >
                      <button className="flex items-center gap-1 hover:text-yellow-400 transition-colors py-4">
                        {link.name}
                        <ChevronDown size={16} />
                      </button>
                      {servicesOpen && (
                        <div className="absolute top-full left-0 bg-white text-gray-800 rounded-md shadow-lg py-2 min-w-[280px]">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              className="block px-4 py-3 hover:bg-yellow-400 hover:text-white transition-colors whitespace-nowrap font-medium"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`hover:text-yellow-400 transition-colors ${
                        location.pathname === link.path ? 'text-yellow-400' : ''
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Auth Buttons - Show Dashboard if logged in, otherwise Login/Sign Up */}
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full font-semibold hover:bg-yellow-500 transition-all flex items-center gap-2"
                >
                  <LayoutDashboard size={18} />
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="border border-white px-4 py-2 rounded-full hover:bg-white hover:text-[#1b2d4a] transition-all"
                  >
                    LogIn
                  </Link>
                  <Link
                    to="/signup"
                    className="border border-white px-4 py-2 rounded-full hover:bg-white hover:text-[#1b2d4a] transition-all"
                  >
                    Sign Up
                  </Link>
                </>
              )}
              <Link
                to="/#download-report"
                className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full font-semibold hover:bg-yellow-500 transition-all flex items-center gap-2"
              >
                Download Stock idea/Sample Research Report →
              </Link>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
